import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, Modal, Form, InputGroup, Pagination } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './FacturesImpayees.css';

export default function FacturesImpayees() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacture, setSelectedFacture] = useState(null);
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [showHistoriqueModal, setShowHistoriqueModal] = useState(false);
  const [showRelanceModal, setShowRelanceModal] = useState(false);
  const [sendingRelance, setSendingRelance] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [paiementData, setPaiementData] = useState({
    montant: '',
    mode_reglement: 'especes',
    reference: '',
    date_reglement: new Date().toISOString().split('T')[0]
  });

  const [relanceData, setRelanceData] = useState({
    type: 'standard',
    message_personnalise: ''
  });

  const [notification, setNotification] = useState({ show: false, type: '', title: '', message: '' });

  const showNotification = (type, title, message) => {
    setNotification({ show: true, type, title, message });
    setTimeout(() => setNotification({ show: false, type: '', title: '', message: '' }), 4000);
  };

  useEffect(() => {
    fetchFacturesImpayees();
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchFacturesImpayees, 60000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchFacturesImpayees = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/factures_impayees.php`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistorique = async (facture) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE_URL}/factures_impayees.php?action=paiements&id=${facture.id_facture}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      setSelectedFacture({ ...facture, paiements: result.paiements });
      setShowHistoriqueModal(true);
    } catch (err) {
      showNotification('error', 'Erreur', "Impossible de charger l'historique");
    }
  };

  const handleSaisirPaiement = (facture) => {
    setSelectedFacture(facture);
    setPaiementData({
      ...paiementData,
      montant: facture.reste_a_payer,
      date_reglement: new Date().toISOString().split('T')[0]
    });
    setShowPaiementModal(true);
  };

  const handlePaiementSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      
      if (!user || !user.id) {
        showNotification('error', 'Erreur', "Utilisateur non identifié");
        return;
      }

      const response = await fetch(`${API_BASE_URL}/factures.php`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'encaisser',
          id_facture: selectedFacture.id_facture,
          montant: parseFloat(paiementData.montant),
          mode_reglement: paiementData.mode_reglement,
          reference: paiementData.reference,
          date_reglement: paiementData.date_reglement,
          id_utilisateur: user.id
        })
      });
      
      if (response.ok) {
        setShowPaiementModal(false);
        fetchFacturesImpayees();
        showNotification('success', 'Succès', "Paiement enregistré avec succès");
        setPaiementData({ montant: '', mode_reglement: 'especes', reference: '', date_reglement: new Date().toISOString().split('T')[0] });
      } else {
        const error = await response.json();
        showNotification('error', 'Erreur', error.message || "Échec de l'enregistrement");
      }
    } catch (err) {
      showNotification('error', 'Erreur', "Erreur de connexion au serveur");
    }
  };

  const handleEnvoyerRelance = (facture) => {
    setSelectedFacture(facture);
    setShowRelanceModal(true);
  };

  const handleConfirmerRelance = async () => {
    if (!selectedFacture) return;
    setSendingRelance(true);
    try {
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;
      
      const response = await fetch(`${API_BASE_URL}/relances.php`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_facture: selectedFacture.id_facture,
          id_utilisateur: user?.id,
          type_relance: relanceData.type,
          message: relanceData.message_personnalise
        })
      });

      const result = await response.json();
      if (result.success) {
        setShowRelanceModal(false);
        showNotification('success', 'Succès', "Relance envoyée avec succès");
        setRelanceData({ type: 'standard', message_personnalise: '' });
      } else {
        showNotification('error', 'Erreur', result.message || "Échec de l'envoi");
      }
    } catch (err) {
      showNotification('error', 'Erreur', "Erreur de connexion au serveur");
    } finally {
      setSendingRelance(false);
    }
  };

  const getPrioriteBadge = (priorite, jours) => {
    const badges = {
      'expiree': { bg: '#dc3545', text: `Expirée (${jours}j)`, icon: 'ri-alert-line', bgSoft: '#fef2f2' },
      'urgente': { bg: '#f59e0b', text: `Urgente (J-${jours})`, icon: 'ri-alarm-warning-line', bgSoft: '#fffbeb' },
      'normale': { bg: '#10b981', text: `Dans ${jours}j`, icon: 'ri-check-line', bgSoft: '#ecfdf5' }
    };
    const badge = badges[priorite] || badges.normale;
    return (
      <span className="status-badge" style={{ background: badge.bgSoft, color: badge.bg }}>
        <i className={`${badge.icon} me-1 fs-10`}></i>
        {badge.text}
      </span>
    );
  };

  const getModePaiementIcon = (mode) => {
    const icons = { 
      'especes': 'ri-money-cny-circle-line', 
      'cheque': 'ri-bank-line', 
      'virement': 'ri-bank-card-line', 
      'carte_bancaire': 'ri-bank-card-2-line', 
      'traite': 'ri-file-copy-line' 
    };
    return icons[mode] || 'ri-bank-line';
  };

  const filteredFactures = data?.factures?.filter(facture => {
    const matchesSearch = facture.numero_facture.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         facture.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         facture.code_client.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || facture.etat_paiement === filter;
    return matchesSearch && matchesFilter;
  }) || [];

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

  const seriesEvolution = [{
    name: 'Montant impayé',
    data: data?.evolution?.map(item => item.montant_impaye) || []
  }];

  const optionsEvolution = {
    chart: { type: 'area', height: 300, toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif' },
    colors: ['#ef4444'],
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.1 } },
    stroke: { curve: 'smooth', width: 2 },
    grid: { borderColor: '#e9ecef', strokeDashArray: 5 },
    xaxis: { categories: data?.evolution?.map(item => { const [year, month] = item.periode.split('-'); return `${month}/${year}`; }) || [], labels: { style: { fontSize: '11px' } } },
    yaxis: { labels: { formatter: (value) => formatCurrency(value), style: { fontSize: '11px' } } },
    tooltip: { y: { formatter: (value) => formatCurrency(value) } }
  };

  if (loading && !data) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="loading-animation mb-4">
                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                <i className="ri-error-warning-line loading-icon"></i>
              </div>
              <h5 className="fw-bold text-primary mb-2">Chargement des factures impayées</h5>
              <p className="text-muted small mb-0">Analyse des créances en cours...</p>
              <div className="mx-auto mt-4" style={{ width: '200px' }}>
                <div className="progress" style={{ height: '3px', background: '#e9ecef' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
          .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
          .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
          .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
          .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
          @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
          @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-3">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchFacturesImpayees}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {notification.show && (
          <div className={`notification notification-${notification.type} show`}>
            <div className="notification-icon"><i className={notification.type === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i></div>
            <div className="notification-content">
              <h6 className="notification-title">{notification.title}</h6>
              <p className="notification-message">{notification.message}</p>
            </div>
            <button className="notification-close" onClick={() => setNotification({ show: false, type: '', title: '', message: '' })}>
              <i className="ri-close-line"></i>
            </button>
            <div className="notification-progress"></div>
          </div>
        )}

        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-danger bg-opacity-10 rounded-3">
                <i className="ri-error-warning-line fs-20 text-danger"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">Gestion des impayés</h4>
                <p className="text-muted small mb-0">Suivi et recouvrement des créances</p>
              </div>
            </div>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch d-flex align-items-center">
              <input className="form-check-input me-2" type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} id="autoRefresh" />
              <label className="form-check-label small text-muted" htmlFor="autoRefresh">Auto-refresh</label>
            </div>
            <Button variant="outline-primary" size="sm" onClick={fetchFacturesImpayees} className="rounded-pill px-3 d-flex align-items-center gap-1">
              <i className="ri-refresh-line"></i> Actualiser
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-danger">
              <div className="kpi-icon"><i className="ri-error-warning-line fs-24"></i></div>
              <div className="kpi-content">
                <span className="kpi-label">Total impayés</span>
                <h3 className="kpi-value">{formatCurrency(data?.statistiques?.total_impaye || 0)}</h3>
                <span className="kpi-unit">{data?.statistiques?.nb_factures_impayees || 0} factures</span>
              </div>
            </div>
          </Col>
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon"><i className="ri-timer-line fs-24"></i></div>
              <div className="kpi-content">
                <span className="kpi-label">Montant expiré</span>
                <h3 className="kpi-value">{formatCurrency(data?.statistiques?.total_expire || 0)}</h3>
                <span className="kpi-unit">Retard max: {data?.statistiques?.retard_max_jours || 0}j</span>
              </div>
            </div>
          </Col>
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-info">
              <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
              <div className="kpi-content">
                <span className="kpi-label">Clients concernés</span>
                <h3 className="kpi-value">{data?.statistiques?.nb_clients_concernes || 0}</h3>
                <span className="kpi-unit">Montant moyen: {formatCurrency(data?.statistiques?.montant_moyen || 0)}</span>
              </div>
            </div>
          </Col>
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon"><i className="ri-calendar-check-line fs-24"></i></div>
              <div className="kpi-content">
                <span className="kpi-label">À encaisser (30j)</span>
                <h3 className="kpi-value">{formatCurrency(data?.previsions?.trente_jours || 0)}</h3>
                <span className="kpi-unit">{data?.previsions?.nb_factures || 0} factures</span>
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-bar-chart-line text-danger"></i>
                  <h6 className="mb-0 fw-semibold">Évolution des impayés (12 mois)</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <ReactApexChart series={seriesEvolution} options={optionsEvolution} type="area" height={300} />
              </Card.Body>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-group-line text-primary"></i>
                  <h6 className="mb-0 fw-semibold">Top clients débiteurs</h6>
                </div>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <tbody>
                      {data?.clients?.slice(0, 5).map((client, idx) => (
                        <tr key={client.id_client}>
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <div>
                              <span className="d-block fw-semibold small">{client.raison_sociale}</span>
                              <small className="text-muted">{client.code_client}</small>
                            </div>
                          </td>
                          <td className="text-end">
                            <span className="fw-bold text-danger small">{formatCurrency(client.total_du)}</span>
                            <br />
                            <small className="text-muted">{client.nb_factures_impayees} facture(s)</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-muted">Paiements partiels</span>
                  <Badge bg="warning" className="rounded-pill">{data?.statistiques?.nb_paiements_partiels || 0}</Badge>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="border-0 shadow-sm rounded-0 mb-4">
          <Card.Body className="py-3 px-4">
            <Row className="align-items-center g-3">
              <Col md="6">
                <div className="position-relative">
                  <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                  <Form.Control type="text" placeholder="Rechercher facture ou client..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="ps-5 rounded-pill" />
                </div>
              </Col>
              <Col md="4">
                <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-pill px-3">
                  <option value="all">Tous les états</option>
                  <option value="impayee">🔴 Impayées</option>
                  <option value="partielle">🟡 Paiements partiels</option>
                </Form.Select>
              </Col>
              <Col md="2">
                <div className="text-end">
                  <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                    <i className="ri-stack-line me-1"></i>{filteredFactures.length} facture(s)
                  </span>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="border-0 shadow-sm rounded-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4">Facture</th>
                    <th>Date</th>
                    <th>Client</th>
                    <th>Échéance</th>
                    <th className="text-end">Montant TTC</th>
                    <th className="text-end">Réglé</th>
                    <th className="text-end">Reste</th>
                    <th>Retard</th>
                    <th className="text-center pe-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? currentItems.map((facture) => (
                    <tr key={facture.id_facture} className={facture.priorite === 'expiree' ? 'table-danger' : ''}>
                      <td className="ps-4 fw-semibold">
                        <Link to={`/ventes/factures/${facture.id_facture}`} className="table-link">{facture.numero_facture}</Link>
                      </td>
                      <td className="text-muted">{formatDate(facture.date_facture)}</td>
                      <td>
                        <div className="fw-semibold">{facture.client}</div>
                        <small className="text-muted">{facture.code_client}</small>
                      </td>
                      <td>
                        <div className="fw-semibold">{formatDate(facture.date_echeance)}</div>
                        <small className={facture.jours_avant_echeance > 0 ? 'text-muted' : 'text-danger'}>
                          {facture.jours_avant_echeance > 0 ? `J-${facture.jours_avant_echeance}` : `J+${Math.abs(facture.jours_retard)}`}
                        </small>
                      </td>
                      <td className="text-end">{formatCurrency(facture.montant_ttc)}</td>
                      <td className="text-end text-success">{formatCurrency(facture.montant_regle)}</td>
                      <td className="text-end text-danger fw-bold">{formatCurrency(facture.reste_a_payer)}</td>
                      <td>{getPrioriteBadge(facture.priorite, facture.jours_retard || Math.abs(facture.jours_avant_echeance))}</td>
                      <td className="text-center pe-4">
                        <div className="d-flex gap-2 justify-content-center">
                          <button className="action-icon" onClick={() => handleViewHistorique(facture)} title="Historique"><i className="ri-history-line"></i></button>
                          <button className="action-icon action-icon-success" onClick={() => handleSaisirPaiement(facture)} title="Saisir un paiement"><i className="ri-bank-card-line"></i></button>
                          <button className="action-icon action-icon-warning" onClick={() => handleEnvoyerRelance(facture)} title="Envoyer une relance"><i className="ri-mail-send-line"></i></button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <i className="ri-checkbox-circle-line fs-48 text-success mb-3 d-block"></i>
                        <h5 className="mb-1">Aucune facture impayée</h5>
                        <p className="text-muted small mb-0">Toutes les factures sont soldées</p>
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-light fw-bold">
                  <tr>
                    <td colSpan="6" className="text-end ps-4">TOTAL:</td>
                    <td className="text-end text-danger">{formatCurrency(filteredFactures.reduce((acc, f) => acc + f.reste_a_payer, 0))}</td>
                    <td colSpan="2"></td>
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-4 px-3">
            <div className="text-muted small">Page {currentPage} sur {totalPages}</div>
            <Pagination className="mb-0">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <Pagination.Item key={page} active={page === currentPage} onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                  {page}
                </Pagination.Item>
              ))}
            </Pagination>
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Lignes par page:</span>
              <select className="form-select form-select-sm" style={{ width: '70px' }} value={itemsPerPage} onChange={() => {}} disabled>
                <option>10</option>
              </select>
            </div>
          </div>
        )}

        <Footer />
      </div>

      <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 rounded-0 bg-success bg-opacity-10"><i className="ri-bank-card-line fs-20 text-success"></i></div>
            <Modal.Title className="fs-5 fw-bold">Enregistrer un paiement</Modal.Title>
          </div>
        </Modal.Header>
        <Form onSubmit={handlePaiementSubmit}>
          <Modal.Body className="px-4 pb-4">
            {selectedFacture && (
              <>
                <Alert variant="light" className="mb-4 p-3 rounded-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{selectedFacture.numero_facture}</strong>
                      <div className="small text-muted">{selectedFacture.client}</div>
                    </div>
                    <Badge bg="danger" className="px-3 py-2 rounded-pill">Reste: {formatCurrency(selectedFacture.reste_a_payer)}</Badge>
                  </div>
                </Alert>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Date de paiement</Form.Label>
                      <Form.Control type="date" value={paiementData.date_reglement} onChange={(e) => setPaiementData({...paiementData, date_reglement: e.target.value})} required className="form-control-modern" />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold small">Montant</Form.Label>
                      <InputGroup>
                        <Form.Control type="number" step="0.01" min="0.01" max={selectedFacture.reste_a_payer} value={paiementData.montant} onChange={(e) => setPaiementData({...paiementData, montant: parseFloat(e.target.value)})} required className="form-control-modern" />
                        <InputGroup.Text className="bg-light border">FCFA</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted small">Max: {formatCurrency(selectedFacture.reste_a_payer)}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold small">Mode de règlement</Form.Label>
                  <Form.Select value={paiementData.mode_reglement} onChange={(e) => setPaiementData({...paiementData, mode_reglement: e.target.value})} required className="form-select-modern">
                    <option value="especes">💰 Espèces</option>
                    <option value="cheque">📝 Chèque</option>
                    <option value="virement">🏦 Virement</option>
                    <option value="carte_bancaire">💳 Carte bancaire</option>
                    <option value="traite">📄 Traite</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold small">Référence (optionnel)</Form.Label>
                  <Form.Control type="text" placeholder="N° chèque, référence virement..." value={paiementData.reference} onChange={(e) => setPaiementData({...paiementData, reference: e.target.value})} className="form-control-modern" />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-4">
            <Button variant="light" onClick={() => setShowPaiementModal(false)} className="rounded-pill px-4">Annuler</Button>
            <Button variant="success" type="submit" className="rounded-pill px-4">Enregistrer le paiement</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showHistoriqueModal} onHide={() => setShowHistoriqueModal(false)} size="lg" centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 rounded-0 bg-info bg-opacity-10"><i className="ri-history-line fs-20 text-info"></i></div>
            <Modal.Title className="fs-5 fw-bold">Historique des paiements</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          {selectedFacture && (
            <>
              <Alert variant="light" className="mb-4 p-3 rounded-0">
                <div className="d-flex justify-content-between">
                  <div>
                    <strong>Facture {selectedFacture.numero_facture}</strong>
                    <div className="small text-muted">Client: {selectedFacture.client}</div>
                  </div>
                  <Badge bg="primary" className="px-3 py-2 rounded-pill">Total: {formatCurrency(selectedFacture.montant_ttc)}</Badge>
                </div>
              </Alert>
              {selectedFacture.paiements?.length > 0 ? (
                <Table striped hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>N° Opération</th>
                      <th>Mode</th>
                      <th>Référence</th>
                      <th className="text-end">Montant</th>
                      <th>Statut</th>
                      <th>Saisi par</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedFacture.paiements.map((paiement, idx) => (
                      <tr key={idx}>
                        <td>{formatDate(paiement.date_reglement)}</td>
                        <td className="font-monospace">{paiement.numero_reglement}</td>
                        <td><i className={`${getModePaiementIcon(paiement.mode_reglement)} me-2`}></i>{paiement.mode_reglement}</td>
                        <td>{paiement.reference || '-'}</td>
                        <td className="text-end fw-bold text-success">{formatCurrency(paiement.montant)}</td>
                        <td><Badge bg={paiement.statut === 'valide' ? 'success' : 'warning'} className="rounded-pill">{paiement.statut}</Badge></td>
                        <td><small>{paiement.prenom_utilisateur} {paiement.nom_utilisateur}</small></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-bold">
                      <td colSpan="4" className="text-end">TOTAL PAYÉ:</td>
                      <td className="text-end text-success">{formatCurrency(selectedFacture.paiements.reduce((acc, p) => acc + p.montant, 0))}</td>
                      <td colSpan="2"></td>
                    </tr>
                  </tfoot>
                </Table>
              ) : (
                <div className="text-center py-5">
                  <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">Aucun paiement enregistré pour cette facture</p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4">
          <Button variant="light" onClick={() => setShowHistoriqueModal(false)} className="rounded-pill px-4">Fermer</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRelanceModal} onHide={() => setShowRelanceModal(false)} centered className="modern-modal">
        <Modal.Header closeButton className="border-0 pt-4 px-4">
          <div className="d-flex align-items-center gap-2">
            <div className="p-2 rounded-0 bg-warning bg-opacity-10"><i className="ri-mail-send-line fs-20 text-warning"></i></div>
            <Modal.Title className="fs-5 fw-bold">Envoyer une relance</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 pb-4">
          {selectedFacture && (
            <>
              <Alert variant="light" className="mb-4 p-3 rounded-0">
                <div className="d-flex justify-content-between">
                  <div>
                    <strong>{selectedFacture.numero_facture}</strong>
                    <div className="small text-muted">{selectedFacture.client}</div>
                  </div>
                  <Badge bg="danger" className="px-3 py-2 rounded-pill">Reste: {formatCurrency(selectedFacture.reste_a_payer)}</Badge>
                </div>
              </Alert>
              <Form.Group className="mb-4">
                <Form.Label className="fw-semibold small">Type de relance</Form.Label>
                <Form.Select value={relanceData.type} onChange={(e) => setRelanceData({...relanceData, type: e.target.value})} className="form-select-modern">
                  <option value="standard">Standard</option>
                  <option value="premiere">Première relance</option>
                  <option value="deuxieme">Deuxième relance - Urgent</option>
                  <option value="derniere">Dernière relance - Mise en demeure</option>
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold small">Message personnalisé (optionnel)</Form.Label>
                <Form.Control as="textarea" rows={3} value={relanceData.message_personnalise} onChange={(e) => setRelanceData({...relanceData, message_personnalise: e.target.value})} placeholder="Ajoutez un message personnalisé si nécessaire..." className="form-control-modern" />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4">
          <Button variant="light" onClick={() => setShowRelanceModal(false)} className="rounded-pill px-4">Annuler</Button>
          <Button variant="warning" onClick={handleConfirmerRelance} disabled={sendingRelance} className="rounded-pill px-4">
            {sendingRelance ? <><Spinner size="sm" className="me-2" />Envoi...</> : <><i className="ri-mail-send-line me-2"></i>Envoyer</>}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
        .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
        .kpi-danger { border-left-color: #dc3545;border-radius: 0px; } .kpi-warning { border-radius: 0px;border-left-color: #f59e0b;border-radius: 0px; } .kpi-info { border-left-color: #0dcaf0;border-radius: 0px; } .kpi-success { border-left-color: #10b981;border-radius: 0px; border-left: 2px solid }
        .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
        .kpi-danger .kpi-icon { background: #fef2f2; color: #dc3545; } .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
        .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; } .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
        .kpi-content { flex: 1; } .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
        .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; } .kpi-unit { font-size: 0.7rem; color: #6c757d; }
        .status-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.75rem; font-weight: 500; }
        .action-icon { width: 32px; height: 32px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; transition: all 0.2s; cursor: pointer; }
        .action-icon:hover { background: #4361ee; border-color: #4361ee; color: white; }
        .action-icon-success:hover { background: #198754; border-color: #198754; color: white; }
        .action-icon-warning:hover { background: #504f4cff; border-color: #f59e0b; color: white; }
        .table-link { color: #4361ee; text-decoration: none; font-weight: 600; } .table-link:hover { text-decoration: underline; }
        .form-control-modern, .form-select-modern { border: 2px solid #e2e8f0; border-radius: 12px; padding: 0.6rem 1rem; transition: all 0.3s ease; }
        .form-control-modern:focus, .form-select-modern:focus { border-color: #4361ee; box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15); outline: none; }
        .rounded-3 { border-radius: 0px !important; }
        .fs-10 { font-size: 0.625rem; } .fs-14 { font-size: 0.875rem; }
        .notification { position: fixed; top: 20px; right: 20px; z-index: 9999; min-width: 350px; background: white; border-radius: 16px; box-shadow: 0 10px 30px rgba(0,0,0,0.15); display: flex; align-items: center; gap: 15px; transform: translateX(120%); transition: transform 0.3s ease; opacity: 0; border-left: 4px solid; overflow: hidden; }
        .notification.show { transform: translateX(0); opacity: 1; }
        .notification-success { border-left-color: #10b981; } .notification-error { border-left-color: #ef4444; }
        .notification-success .notification-icon { background: linear-gradient(135deg, #10b981, #059669); }
        .notification-error .notification-icon { background: linear-gradient(135deg, #ef4444, #dc2626); }
        .notification-icon { width: 45px; height: 45px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; flex-shrink: 0; }
        .notification-content { flex: 1; }
        .notification-title { margin: 0 0 4px 0; font-size: 1rem; font-weight: 600; color: #1e293b; }
        .notification-message { margin: 0; font-size: 0.9rem; color: #64748b; }
        .notification-close { background: transparent; border: none; color: #94a3b8; font-size: 18px; cursor: pointer; padding: 5px; display: flex; align-items: center; justify-content: center; }
        .notification-close:hover { color: #1e293b; }
        .notification-progress { position: absolute; bottom: 0; left: 0; height: 3px; width: 100%; background: rgba(0,0,0,0.1); animation: progress 4s linear forwards; }
        .notification-success .notification-progress { background: linear-gradient(90deg, #10b981, #059669); }
        .notification-error .notification-progress { background: linear-gradient(90deg, #ef4444, #dc2626); }
        @keyframes progress { 0% { width: 100%; } 100% { width: 0%; } }
        @media (max-width: 768px) { .notification { min-width: auto; width: calc(100% - 20px); max-width: none; top: 10px; right: 10px; left: 10px; } }
        .modern-modal .modal-content { border: none; border-radius: 24px; overflow: hidden; }
      `}</style>
    </>
  );
}